import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../components/Icon";
import { deactivateFamilyMember, getFamilyMembers } from "../../lib/api-family";
import type { FamilyMember } from "../../types/family";
import { EditFamilyNameModal } from "./EditFamilyNameModal";
import { MembershipActionConfirm, type MembershipAction } from "./MembershipActionConfirm";

type State = { status: "loading" } | { status: "error" } | { status: "loaded"; members: FamilyMember[] };
type SelectedAction = { action: MembershipAction; member: FamilyMember };

export function FamilyMembersList({
  familyId,
  currentRole,
  onLeft,
}: {
  familyId: string;
  currentRole: "admin" | "member";
  onLeft: () => void;
}) {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ status: "loading" });
  const [selected, setSelected] = useState<SelectedAction | null>(null);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const load = useCallback(async () => {
    setState({ status: "loading" });
    try { setState({ status: "loaded", members: await getFamilyMembers(familyId) }); }
    catch { setState({ status: "error" }); }
  }, [familyId]);

  useEffect(() => { void load(); }, [load]);

  const choose = (action: MembershipAction, member: FamilyMember) => {
    setActionError(null);
    setSelected({ action, member });
  };

  const confirm = async () => {
    if (!selected) return;
    setPending(true);
    setActionError(null);
    try {
      await deactivateFamilyMember(familyId, selected.member.id);
      if (selected.action === "leave") {
        onLeft();
        return;
      }
      setSelected(null);
      await load();
    } catch {
      setActionError(selected.action === "leave"
        ? "Не удалось покинуть семью. Попробуйте ещё раз."
        : "Не удалось удалить участника. Попробуйте ещё раз.");
    } finally {
      setPending(false);
    }
  };

  return <section className="mx-auto w-full max-w-3xl px-5 pb-10 pt-8 lg:px-0" aria-labelledby="family-members-heading">
    <h2 id="family-members-heading" className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF7A00]">Участники</h2>
    {state.status === "loading" && <div className="h-28 animate-pulse rounded-[24px] bg-white" aria-label="Загрузка участников" />}
    {state.status === "error" && <div className="rounded-[24px] bg-white p-6 text-center"><p className="text-sm text-[#77776F]">Не удалось загрузить участников.</p><button type="button" onClick={() => void load()} className="mt-4 rounded-full bg-[#171714] px-5 py-2 text-sm font-semibold text-white">Повторить</button></div>}
    {state.status === "loaded" && state.members.length === 0 && <div className="rounded-[24px] bg-white p-6 text-sm text-[#77776F]">Активных участников пока нет.</div>}
    {state.status === "loaded" && state.members.length > 0 && <div className="grid gap-3 sm:grid-cols-2">{state.members.map((member) => {
      const canLeave = currentRole === "member" && member.is_current;
      const canRemove = currentRole === "admin" && !member.is_current && member.role === "member";
      return <article key={member.id} className="rounded-[22px] bg-white p-4 shadow-[0_10px_28px_rgba(35,33,25,0.06)]">
        <button type="button" onClick={() => navigate(`/members/${member.id}/wishes`)} disabled={pending} aria-label={`Открыть желания: ${member.name}`} className="flex w-full items-center gap-4 text-left disabled:cursor-wait">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-[#FFF0DF] text-[#FF7A00]">{member.avatar_url ? <img src={member.avatar_url} alt="" className="h-full w-full object-cover" /> : <Icon name="family" size={23} />}</div>
          <div className="min-w-0 flex-1"><p className="truncate font-bold text-[#171714]">{member.name}{member.is_current ? " · Вы" : ""}</p><p className="mt-0.5 text-xs text-[#77776F]">{member.role === "admin" ? "Администратор" : "Участник"}</p></div><Icon name="gift" size={19} className="text-[#77776F]" />
        </button>
        {member.is_current && <button type="button" disabled={pending} onClick={() => setEditingMember(member)} className="mt-3 w-full rounded-xl border border-line px-4 py-2 text-sm font-bold text-content disabled:cursor-wait disabled:opacity-60">
          Изменить имя
        </button>}
        {(canLeave || canRemove) && <button type="button" disabled={pending} onClick={() => choose(canLeave ? "leave" : "remove", member)} className="mt-3 w-full rounded-xl bg-[#FEF2F2] px-4 py-2 text-sm font-bold text-[#B91C1C] disabled:cursor-wait disabled:opacity-60">
          {canLeave ? "Покинуть семью" : "Удалить участника"}
        </button>}
      </article>;
    })}</div>}
    {editingMember && <EditFamilyNameModal familyId={familyId} member={editingMember} onClose={() => setEditingMember(null)} onUpdated={() => { setEditingMember(null); void load(); }} />}
    {selected && <MembershipActionConfirm action={selected.action} member={selected.member} pending={pending} error={actionError} onClose={() => setSelected(null)} onConfirm={() => void confirm()} />}
  </section>;
}
